import React, { useEffect, useState } from "react";
import OperationApi from "../../services/OperationApi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import { useToast } from "../../context/ToastContext";
import { Column } from "primereact/column";
import { useAuth } from "../../context/AuthContext";
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input, { type InputOption } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tag } from "../../components/Tag/Tag";
import PageHeader from "../../components/PageHeader/PageHeader";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "TableFilterAdvanced_filters";

// #region Interfaces
interface Operation {
  id: string;
  name: string;
  description: string;
  rank: number;
  isActive: boolean;
  subLine: {
    id: string;
    name: string;
    line: {
      name: string;
      area: {
        name: string;
        productionUnit: {
          name: string;
        };
      };
    };
  };
  operationType: {
    id: string;
    name: string;
  };
}

interface OperationTableRow extends Operation {
  subline: string;
  line: string;
  area: string;
  productionunit: string;
  operationtype: string; // nome legÃ­vel
  operationTypeIds: string[]; // <-- ARRAY para o filtro
}

const ListOperation: React.FC = () => {
  const { plantId } = useAuth();
  const [operations, setOperations] = useState<Operation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(["form", "general"]);
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const [productionUnits, setProductionUnits] = useState<InputOption[]>([]);
  const [areas, setAreas] = useState<InputOption[]>([]);
  const [lines, setLines] = useState<InputOption[]>([]);
  const [sublines, setSublines] = useState<InputOption[]>([]);
  
  // estados de paginaÃ§Ã£o/ordenaÃ§Ã£o
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Define a funÃ§Ã£o onSort para atualizar sortField e sortOrder
  const onSort = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
    fetchOperations({}, page, e.sortField, e.sortOrder === 1 ? "asc" : "desc");
  };

  // Estados dos filtros individuais
  const [filterName, setFilterName] = useState<string>("");
  const [filterRank, setFilterRank] = useState<number | undefined>(undefined);
  const [filterProductionUnit, setFilterProductionUnit] = useState<string[]>([]);
  const [filterArea, setFilterArea] = useState<string[]>([]);
  const [filterLine, setFilterLine] = useState<string[]>([]);
  const [filterSubline, setFilterSubline] = useState<string[]>([]);
  const [filterOperationType, setFilterOperationType] = useState<string[]>([]);
  const [filterIsActive, setFilterIsActive] = useState<boolean[]>([]);

  // Carrega filtros salvos do localStorage
  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    if (!plantId) {
      return;
    }
    fetchProductionUnits();
    fetchAreas();
    fetchLines();
    fetchSubline();
    fetchOperations(savedFilters, 0); // Busca inicial automÃ¡tica
  }, [plantId]);


  // #region Fetchs

  const fetchProductionUnits: any = async () => {
    try {
      const response = await ProductionUnitApi.GetProductUnitByPlant(plantId);
      const units = response.data.map((unit: any) => ({
        id: unit.id,
        description: unit.name,
        label: unit.name,
        value: unit.id,
      }));
      setProductionUnits(units);
    } catch (err) {
      return;
    }
  };

  const fetchAreas: any = async () => {
    try {
      const response = await AreaApi.GetAreaByPlantIdStatic(plantId);
      const area = response.data.map((unit: any) => ({
        id: unit.id,
        description: unit.name,
        label: unit.name,
        value: unit.id,
      }));
      setAreas(area);
    } catch (err) {
      return;
    }
  };

  const fetchLines: any = async () => {
    try {
      const response = await LineApi.GetLinesByPlant(plantId);
      const units = response.data.map((unit: any) => ({
        id: unit.id,
        description: unit.name,
        label: unit.name,
        value: unit.id,
      }));
      setLines(units);
    } catch (err) {
      return;
    }
  };

  const fetchSubline: any = async () => {
    try {
      const response = await SublineApi.GetSublineByPlant(plantId);
      const units = response.data.map((unit: any) => ({
        id: unit.id,
        description: unit.name,
        label: unit.name,
        value: unit.id,
      }));
      setSublines(units);
    } catch (err) {
      return;
    }
  };

  const fetchOperations = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    setIsLoading(true);

    const name = filters.name?.value || undefined;
    const rank = filters.rank?.value ?? undefined;
    const isActive: boolean[] = filters.isActive?.value ?? [];
    const isActiveParam: boolean | undefined =
      isActive.length === 1 ? isActive[0] : undefined;

    const operationTypeIds: string[] = filters.operationTypeIds?.value || [];

    const plantIds: string[] =
      filters.plant?.value || (plantId ? [plantId] : []);
    const prodIds: string[] = filters.productionunit?.value || [];
    const areaIds: string[] = filters.area?.value || [];
    const lineIds: string[] = filters.line?.value || [];
    const subIds: string[] = filters.subline?.value || [];

    try {
      const response = await OperationApi.GetByFilter(
        plantIds,
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        name,
        /* description */ undefined,
        rank,
        isActiveParam,
        prodIds,
        areaIds,
        lineIds,
        subIds,
        operationTypeIds,
      );

      const data = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.operations)
          ? response.data.operations
          : [];

      setTotalRecords(response.data.total || data.length);

      setOperations(
        data.map((op: Operation): any => ({
          ...op,
          subline: op.subLine?.name || "N/A",
          line: op.subLine?.line?.name || "N/A",
          area: op.subLine?.line?.area?.name || "N/A",
          productionunit: op.subLine?.line?.area?.productionUnit?.name || "N/A",
          operationtype: op.operationType?.name || "N/A",
          operationTypeIds: [op.operationType.id],
          isActive: op.isActive,
        })),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const operationTypeOptions = Array.from(
    new Map(
      operations.map((op) => [
        op.operationType.id,
        {
          id: op.operationType.id,
          description: op.operationType.name,
          label: op.operationType.name,
          value: op.operationType.id
        },
      ]),
    ).values(),
  );

  const isActiveOptions = [
    {
      id: 'active',
      description: t("form:active"),
      label: t("form:active"),
      value: true
    },
    {
      id: 'inactive',
      description: t("form:inactive"),
      label: t("form:inactive"),
      value: false
    },
  ];

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      rank: filterRank !== undefined ? { value: filterRank } : undefined,
      productionunit: filterProductionUnit.length > 0 ? { value: filterProductionUnit } : undefined,
      area: filterArea.length > 0 ? { value: filterArea } : undefined,
      line: filterLine.length > 0 ? { value: filterLine } : undefined,
      subline: filterSubline.length > 0 ? { value: filterSubline } : undefined,
      operationTypeIds: filterOperationType.length > 0 ? { value: filterOperationType } : undefined,
      isActive: filterIsActive.length > 0 ? { value: filterIsActive } : undefined,
    };

    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchOperations(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterRank(undefined);
    setFilterProductionUnit([]);
    setFilterArea([]);
    setFilterLine([]);
    setFilterSubline([]);
    setFilterOperationType([]);
    setFilterIsActive([]);
    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchOperations({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchOperations(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  function editOperation(id: string): void {
    navigate(`/operations/${id}/edit`);
  }
  
  async function deleteOperation(id: string): Promise<void> {
    try {
      await OperationApi.Delete(id);
      setOperations((prev) => prev.filter((op) => op.id !== id));
      displayToast(
        "success",
        t("form:success"),
        t("form:operationSuceffullyDeleted"),
        false,
        3000,
      );
    } catch (error: any) {
      displayToast(
        "error",
        t("form:error"),
        error.data.data || t("form:operationNotDeleted"),
        false,
        3000,
      );
    }
  }

  const confirmDelete: any = (id: string): void => {
    confirmDialog({
      message: t("form:operationDeleteMessage"),
      header: t("form:confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: t("form:yes"),
      rejectLabel: t("form:no"),
      acceptClassName: "p-button-accept",
      rejectClassName: "p-button-reject",
      accept: () => deleteOperation(id),
    });
  };

  const actionBodyTemplate = (rowData: OperationTableRow): JSX.Element => {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <IconButton
          icon="visibility"
          variant="text"
          severity="secondary"
          onClick={() => navigate(`/operations/${rowData.id}`)}
          aria-label={t("general:seeDetails")}
        />
        <IconButton
          icon="edit"
          variant="text"
          severity="secondary"
          onClick={() => editOperation(rowData.id)}
          aria-label={t("general:edit")}
        />
        <IconButton
          icon="delete"
          variant="text"
          severity="secondary"
          onClick={() => confirmDelete(rowData.id)}
          aria-label={t("general:delete")}
        />
      </div>
    );
  };

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={t("form:operations")}
        description={t("form:operationDescription") || "Gerencie as operaÃ§Ãµes existentes no sistema e suas configuraÃ§Ãµes"}
      >
        <Button
          label={t("form:register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/operations/create")}
        />
      </PageHeader>
      <div>
        <ConfirmDialog />
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <FilterPanel
            title={t("general:filters") || "Filtros"}
            showFilterIcon
            onApply={handleFilter}
            onClear={handleClearFilters}
          >
            <Input
              type="text"
              label={t("form:name")}
              placeholder={t("form:searchByName") || "Pesquisar por nome"}
              value={filterName}
              onChange={(value) => setFilterName(value)}
            />
            <Input
              type="number"
              label={t("form:rank")}
              placeholder={t("form:enterRank") || "Insira o rank"}
              value={filterRank}
              onChange={(value) => setFilterRank(value)}
            />
            <Input
              type="multiselect"
              label={t("form:productionUnit")}
              placeholder={t("form:selectProductionUnits") || "Selecione as unidades de produÃ§Ã£o"}
              value={filterProductionUnit}
              onChange={(value) => setFilterProductionUnit(value)}
              options={productionUnits}
            />
            <Input
              type="multiselect"
              label={t("form:area")}
              placeholder={t("form:selectAreas") || "Selecione as Ã¡reas"}
              value={filterArea}
              onChange={(value) => setFilterArea(value)}
              options={areas}
            />
            <Input
              type="multiselect"
              label={t("form:line")}
              placeholder={t("form:selectLines") || "Selecione as linhas"}
              value={filterLine}
              onChange={(value) => setFilterLine(value)}
              options={lines}
            />
            <Input
              type="multiselect"
              label={t("form:subline")}
              placeholder={t("form:selectSublines") || "Selecione as sublinhas"}
              value={filterSubline}
              onChange={(value) => setFilterSubline(value)}
              options={sublines}
            />
            <Input
              type="multiselect"
              label={t("form:operationType")}
              placeholder={t("form:selectOperationTypes") || "Selecione os tipos de operaÃ§Ã£o"}
              value={filterOperationType}
              onChange={(value) => setFilterOperationType(value)}
              options={operationTypeOptions}
            />
            <Input
              type="multiselect"
              label={t("form:status")}
              placeholder={t("form:selectStatus") || "Selecione os status"}
              value={filterIsActive}
              onChange={(value) => setFilterIsActive(value)}
              options={isActiveOptions}
            />
          </FilterPanel>

          <DataTable
            value={operations}
            paginated
            first={page}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            emptyMessage={t("form:noRecordsFound") || "Nenhum registro encontrado"}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={onSort}
            resizableColumns
          >
            <Column
              field="name"
              header={t("form:name")}
              sortable
            />
            <Column
              field="productionunit"
              header={t("form:productionUnit")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: OperationTableRow) => rowData.productionunit || "N/A"}
            />
            <Column
              field="area"
              header={t("form:area")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: OperationTableRow) => rowData.area || "N/A"}
            />
            <Column
              field="line"
              header={t("form:line")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: OperationTableRow) => rowData.line || "N/A"}
            />
            <Column
              field="subline"
              header={t("form:subline")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: OperationTableRow) => rowData.subline || "N/A"}
            />
            <Column
              field="rank"
              header={t("form:rank")}
              sortable
              style={{ width: "6rem" }}
            />
            <Column
              field="operationtype"
              header={t("form:operationType")}
              sortable
              style={{ minWidth: "8rem" }}
              body={(rowData: OperationTableRow) => rowData.operationtype}
            />
            <Column
              field="isActive"
              header={t("form:status")}
              sortable
              style={{ width: "8rem" }}
              body={(rowData: OperationTableRow) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Tag
                    label={rowData.isActive ? t("form:active") : t("form:inactive")}
                    severity={rowData.isActive ? "successInverse" : "errorInverse"}
                  />
                </div>
              )}
            />
            <Column
              field="actions"
              header={t("form:actions")}
              body={actionBodyTemplate}
              style={{ width: "7rem" }}
              resizeable={false}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default ListOperation;
